import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@core/icons";
import { Shell } from "../access/AccessLayout";
import { BigButton, Notice, TextButton } from "../access/primitives";
import { clearValidatorSession, loadValidatorSession } from "../access/session";
import { signOut } from "../access/api";
import { selectScanner } from "../scanner/selectScanner";
import { listValidations } from "./api";
import { SessionHeader } from "./SessionHeader";
import { ManualCodeSheet } from "./ManualCodeSheet";
import "./home.css";

// SPEC-302 · Inicio del validador — `validador.jsx:251-303` (`Inicio`) +
// `:235-247` (`Cabecera`).
function formatToday(): string {
  const raw = new Intl.DateTimeFormat("es-EC", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

interface LocationState {
  openManual?: boolean;
  // SPEC-303 §4/§8 (RF-VAL-28): `ConfirmScreen` navega acá tras cancelar
  // con este aviso — "el código no fue consumido y sigue disponible". El
  // validador no tiene `ToastProvider` (SPEC-012 es exclusivo del portal),
  // así que se reutiliza el patrón propio de esta superficie: un `Notice`
  // transitorio, como ya hace `ScannerScreen.showTransientNotice`.
  notice?: string;
}

export function HomeScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  // `RequireValidatorSession` (App.tsx) ya garantiza sesión válida antes de
  // montar esta pantalla, pero el tipo de `loadValidatorSession()` sigue
  // siendo `| null` — se trata de forma defensiva, sin pantalla propia.
  const session = loadValidatorSession();

  const [manualOpen, setManualOpen] = useState(
    Boolean((location.state as LocationState | null)?.openManual),
  );
  const [cancelNotice, setCancelNotice] = useState<string | null>(
    (location.state as LocationState | null)?.notice ?? null,
  );
  const [blockedUntil, setBlockedUntil] = useState<Date | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  // `selectScanner()` no descarga nada (ADR-012): solo instancia el
  // adaptador para preguntarle si hay cámara disponible en este navegador.
  const [scannerSupported] = useState(() => selectScanner() !== null);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (!cancelNotice) return;
    const timeout = setTimeout(() => {
      setCancelNotice(null);
    }, 4000);
    return () => {
      clearTimeout(timeout);
    };
  }, [cancelNotice]);

  useEffect(() => {
    function handleOnline() {
      setOnline(true);
    }
    function handleOffline() {
      setOnline(false);
    }
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const bearerToken = session?.token ?? "";
  const validationsQuery = useQuery({
    queryKey: ["validator", "validations"],
    queryFn: () => listValidations(bearerToken),
    enabled: Boolean(bearerToken),
  });

  async function handleSignOut() {
    setSigningOut(true);
    try {
      if (bearerToken) {
        await signOut(bearerToken);
      }
    } catch {
      // RF-VAL-11 / ANNEX-011 §2.3: la salida es local de todos modos — si
      // el servidor no responde, igual se limpia la sesión del dispositivo.
    } finally {
      clearValidatorSession();
      navigate("/validator/access-login", { replace: true });
    }
  }

  if (!session) {
    return null;
  }

  const scanDisabled = !online || !scannerSupported;

  return (
    <Shell>
      <SessionHeader
        session={session}
        signingOut={signingOut}
        onSignOut={() => {
          void handleSignOut();
        }}
      />
      <div className="sc-home-content">
        <div className="sc-home-date">{formatToday()}</div>
        <h1 className="sc-home-title">Listo para validar</h1>

        {cancelNotice ? <Notice tone="info">{cancelNotice}</Notice> : null}

        {!online ? (
          <Notice>
            <div>Sin conexión. Revisa la señal o el wifi del local</div>
            <TextButton
              onClick={() => {
                setOnline(navigator.onLine);
                void validationsQuery.refetch();
              }}
            >
              Reintentar
            </TextButton>
          </Notice>
        ) : null}

        {online && !scannerSupported ? (
          <Notice tone="info">
            Este dispositivo no puede usar la cámara para leer códigos. Ingresa
            el código a mano.
          </Notice>
        ) : null}

        <BigButton
          tone="green"
          className="sc-scan-btn"
          disabled={scanDisabled}
          onClick={() => {
            navigate("/validator/scan");
          }}
        >
          <Icon name="qr" size={20} stroke={1.9} />
          Escanear código QR
        </BigButton>
        <button
          type="button"
          className="sc-manual-link"
          onClick={() => {
            setManualOpen(true);
          }}
        >
          Ingresar código manualmente
        </button>
        <div className="sc-manual-help">
          Úsalo si la cámara no abre o el código no se lee.
        </div>

        <div className="sc-section-head">
          <span className="sc-section-label">Validaciones de hoy</span>
          <span className="sc-section-line" />
          <span className="sc-section-count">
            {validationsQuery.data?.dayTotal ?? 0}
          </span>
        </div>

        {validationsQuery.isError ? (
          <Notice>
            <div>No se pudieron cargar las validaciones de hoy</div>
            <TextButton
              onClick={() => {
                void validationsQuery.refetch();
              }}
            >
              Reintentar
            </TextButton>
          </Notice>
        ) : (
          <div className="sc-rows">
            {(validationsQuery.data?.items ?? []).map((validation) => (
              <div key={validation.id} className="sc-row">
                <div className="sc-row-info">
                  <div className="sc-row-member">{validation.member}</div>
                  <div className="sc-row-item">
                    <span
                      className={
                        validation.reversed
                          ? "sc-row-item--reversed"
                          : undefined
                      }
                    >
                      {validation.item}
                    </span>
                    {validation.reversed ? (
                      <span className="sc-row-item-suffix"> · revertido</span>
                    ) : null}
                  </div>
                </div>
                <span className="sc-row-time">{validation.time}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {manualOpen ? (
        <ManualCodeSheet
          bearerToken={bearerToken}
          blockedUntil={blockedUntil}
          offline={!online}
          onBlocked={setBlockedUntil}
          onClose={() => {
            setManualOpen(false);
          }}
        />
      ) : null}
    </Shell>
  );
}
